import { useEffect, useState } from "react";
import { api, fmtDateTime, fmtDate } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, MapPin } from "lucide-react";

export default function Attendance() {
  const [att, setAtt] = useState([]);
  const [leaves, setLeaves] = useState([]);

  const load = () => {
    api.get("/attendance?days=14").then((r) => setAtt(r.data));
    api.get("/leave-requests").then((r) => setLeaves(r.data));
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    await api.post(`/leave-requests/${id}/approve`, { decision });
    toast.success(decision === "approved" ? "Đã duyệt" : "Đã từ chối"); load();
  };

  return (
    <div data-testid="page-attendance">
      <PageHeader kicker="HR-Ops" title="Chấm công & Nghỉ phép" breadcrumb={["HR-Ops", "Chấm công"]} />
      <div className="p-6 lg:p-8">
        <Tabs defaultValue="att">
          <TabsList className="bg-stone-100 rounded-sm">
            <TabsTrigger value="att" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid="tab-attendance">Chấm công ({att.length})</TabsTrigger>
            <TabsTrigger value="leave" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid="tab-leave">Đơn từ ({leaves.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="att" className="mt-4">
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      {["Ngày", "Mã NV", "Họ tên", "Chi nhánh", "Check-in", "Check-out", "GPS", "Số phút", "Trạng thái"].map((h) => (
                        <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {att.map((a) => (
                      <tr key={a.id} className="hover:bg-stone-50">
                        <td className="px-4 py-2 font-mono text-xs">{fmtDate(a.work_date)}</td>
                        <td className="px-4 py-2 font-mono text-xs text-stone-600">{a.emp_code}</td>
                        <td className="px-4 py-2 font-semibold">{a.emp_name}</td>
                        <td className="px-4 py-2 text-stone-600 text-xs">{a.branch_name || "—"}</td>
                        <td className="px-4 py-2 font-mono text-xs">{fmtDateTime(a.check_in_at)}</td>
                        <td className="px-4 py-2 font-mono text-xs">{fmtDateTime(a.check_out_at)}</td>
                        <td className="px-4 py-2 text-xs">
                          {a.check_in_lat ? (
                            <span className="inline-flex items-center gap-1 text-stone-600 font-mono">
                              <MapPin className="w-3 h-3" />{Number(a.check_in_lat).toFixed(3)},{Number(a.check_in_lng).toFixed(3)}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{a.work_minutes}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${
                            a.status === "late" ? "bg-amber-100 text-amber-900" :
                            a.status === "absent" ? "bg-red-100 text-red-900" :
                            "bg-emerald-100 text-emerald-900"}`}>{a.status}</span>
                        </td>
                      </tr>
                    ))}
                    {att.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-stone-400">Chưa có dữ liệu chấm công</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="leave" className="mt-4">
            <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {["NV","Loại","Từ ngày","Đến ngày","Lý do","Trạng thái",""].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {leaves.map((l) => (
                    <tr key={l.id}>
                      <td className="px-4 py-2"><span className="font-semibold">{l.emp_name}</span> <span className="text-xs text-stone-500 font-mono">{l.emp_code}</span></td>
                      <td className="px-4 py-2"><span className="text-xs px-1.5 py-0.5 bg-stone-100 rounded-sm">{l.type}</span></td>
                      <td className="px-4 py-2 font-mono text-xs">{fmtDate(l.from_date)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{fmtDate(l.to_date)}</td>
                      <td className="px-4 py-2 text-stone-600 text-xs">{l.reason || "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${
                          l.status === "approved" ? "bg-emerald-100 text-emerald-900" :
                          l.status === "rejected" ? "bg-red-100 text-red-900" :
                          "bg-amber-100 text-amber-900"}`}>{l.status}</span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {l.status === "pending" && (
                          <>
                            <Button onClick={() => decide(l.id, "approved")} size="sm" variant="outline" className="rounded-sm h-7 mr-1" data-testid={`leave-approve-${l.id}`}><Check className="w-3 h-3 mr-1" />Duyệt</Button>
                            <Button onClick={() => decide(l.id, "rejected")} size="sm" variant="outline" className="rounded-sm h-7" data-testid={`leave-reject-${l.id}`}><X className="w-3 h-3 mr-1" />Từ chối</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {leaves.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">Chưa có đơn từ</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
